// A recording in Vimeo's own player (@vimeo/player, bundled from npm, never a CDN), for every
// product's recordings. Until Play is pressed it's the board's black box (FEWeek.dc.html,
// PhoneWeek.dc.html) with the play button, the recorded line and the bar, so nothing loads from Vimeo
// for someone who only looks. Play, a chapter or a timestamp makes the iframe from the recording's
// link, with its privacy hash (C30), and starts it where they left off, at the chapter or at the
// minute. The Player is given the iframe, so it never asks Vimeo's oEmbed (the site's
// Content-Security-Policy allows only player.vimeo.com in a frame).
//
// Where they are is reported as they watch, for the page to save (useRecordingProgress): every 15
// seconds, straight away the first time it passes the product's watched share, on pause (the start
// again at the very end), at the end and as the page closes. Each report carries the position, the
// furthest second reached this time and how many seconds were actually watched this time.
//
//   <Player title content={recording_content row} savedSeconds startAt={seconds or null}
//     seek={{ n, seconds }} onTime={s => ...} onReport={r => ...} canSave watchedShare
//     poster={{ recorded, line, pct, watched }} />
import { useEffect, useRef, useState } from 'react'
import Player from '@vimeo/player'
import { parseVimeo, vimeoSrc, positionOnPause, SAVE_EVERY_MS } from '../free/vimeo.js'
import { PlayFill, Tick } from './Icons.jsx'

export const NOT_READY = "This recording isn't ready to play yet."

// The recording's Vimeo id and privacy hash, from its link or its own columns
export function vimeoOf(content) {
  if (!content) return null
  const parsed = parseVimeo(content.vimeo_url)
  if (parsed) return { id: parsed.id, hash: parsed.hash || content.vimeo_hash || null }
  if (content.vimeo_id) return { id: String(content.vimeo_id), hash: content.vimeo_hash || null }
  return null
}

// Where Play starts: a minute asked for, else where they left off (more than 10 seconds in, and not
// at the very end), else the top
export function startFrom({ askedSeconds = null, savedSeconds = 0, duration = 0 }) {
  if (askedSeconds != null) return Math.max(0, Math.min(askedSeconds, duration || askedSeconds))
  if (savedSeconds > 10 && (!duration || savedSeconds < duration - 5)) return savedSeconds
  return 0
}

export function RecordingPlayer({ title, content, savedSeconds = 0, startAt = null, seek = null, onTime, onReport, onPlay,
  canSave = true, watchedShare = 0.9, poster = {}, phone = false }) {
  const vimeo = vimeoOf(content)
  const duration = content?.duration_seconds || 0
  const [start, setStart] = useState(null)
  const [notReady, setNotReady] = useState(false)
  const frame = useRef(null)
  const player = useRef(null)
  const cb = useRef({})
  cb.current = { onTime, onReport: canSave ? onReport : null, onPlay }

  function play(at = null) {
    if (!vimeo) { setNotReady(true); return }
    setStart(startFrom({ askedSeconds: at ?? startAt, savedSeconds, duration }))
    cb.current.onPlay?.()
  }

  // a chapter or a timestamp: start there, or move there if it's already playing
  const seekN = seek?.n
  useEffect(() => {
    if (seekN == null) return
    if (player.current) player.current.setCurrentTime(seek.seconds).then(() => player.current?.play()).catch(() => {})
    else play(seek.seconds)
  }, [seekN])

  const loaded = start != null && Boolean(vimeo)
  useEffect(() => {
    if (!loaded || !frame.current) return undefined
    const p = new Player(frame.current)
    player.current = p
    let lastSave = Date.now()
    let last = start
    let furthest = start
    let watched = 0
    let passed = false
    const report = position => cb.current.onReport?.({ position, furthest, watched })
    const onTimeUpdate = e => {
      const s = Math.floor(e.seconds)
      // seconds actually watched: small steps forward while it plays, never a jump
      const step = e.seconds - last
      if (step > 0 && step < 3) watched += step
      last = e.seconds
      if (s > furthest) furthest = s
      cb.current.onTime?.(s)
      if (!passed && e.percent >= watchedShare) {
        passed = true
        lastSave = Date.now()
        report(s)
        return
      }
      if (Date.now() - lastSave >= SAVE_EVERY_MS) { lastSave = Date.now(); report(s) }
    }
    const onPause = e => { report(positionOnPause(e.seconds, e.percent)) }
    const onEnd = () => { furthest = Math.max(furthest, duration); report(0) }
    const onLeave = () => report(Math.floor(last))
    p.on('timeupdate', onTimeUpdate)
    p.on('pause', onPause)
    p.on('ended', onEnd)
    window.addEventListener('pagehide', onLeave)
    return () => {
      window.removeEventListener('pagehide', onLeave)
      try { p.off('timeupdate', onTimeUpdate); p.off('pause', onPause); p.off('ended', onEnd) } catch { /* the frame is going */ }
      report(Math.floor(last))
      player.current = null
    }
  }, [loaded])

  if (loaded) {
    // #t= starts it at the right second without waiting for the player to answer
    const src = vimeoSrc(vimeo, { autoplay: true }) + (start > 0 ? `#t=${start}s` : '')
    return (
      <div className="pp-player pp-player--on">
        <iframe ref={frame} src={src} title={title} allow="autoplay; fullscreen; picture-in-picture; encrypted-media" allowFullScreen />
      </div>
    )
  }
  const line = notReady ? NOT_READY : poster.line
  return (
    <div className="pp-player">
      <button type="button" className="pp-play" aria-label="Play the recording" onClick={() => play()}>
        <PlayFill size={phone ? 26 : 34} />
      </button>
      {poster.recorded && !phone && <span className="pp-player-recorded">{poster.recorded}</span>}
      <div className="pp-player-foot">
        <span className="pp-player-bar"><span style={{ width: `${Math.max(0, Math.min(100, poster.pct || 0))}%` }} /></span>
        <span className="pp-player-line">
          <span role={notReady ? 'status' : undefined}>{line}</span>
          {poster.watched && !phone && <span className="pp-player-watched"><Tick />Watched</span>}
        </span>
      </div>
    </div>
  )
}
